import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    // THEODORE: how focus arrived decides the hold (#hasKeyboardFocus, bug hunt #17), including
    // keyboard focus that reached the link before this script ran.
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    const focused = document.activeElement;
    if (focused && focused !== this && this.contains(focused)) this.#keyboardFocus = this.#focusVisible(focused);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      // THEODORE: also hold the message while a keyboard user is on its link. Rotating under
      // the focused link lost their place on the page (bug hunt #17, 1 Nov 2026).
      if (this.matches(':hover') || this.#hasKeyboardFocus() || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * THEODORE: whether focus inside the bar arrived by keyboard (bug hunt #17). A mouse press
   * focuses the link as well, and holding on that froze the bar after a Ctrl-click opened the
   * link in a new tab. Read once, as focus arrives: Chrome turns :focus-visible on for a
   * mouse-focused link at the next key press (Space or an arrow to scroll the page), which
   * froze it the same way. Where :focus-visible is unknown, any focus holds.
   * @type {boolean}
   */
  #keyboardFocus = false;

  /**
   * THEODORE: the element that kept focus while the window or tab was in the background. Its
   * focusin on return is not a new arrival: by then a key press may have turned :focus-visible
   * on for a mouse-focused link, which froze the bar after a Ctrl-click, a key and a trip to the
   * new tab (bug hunt #17).
   * @type {EventTarget|null}
   */
  #keptFocus = null;

  #hasKeyboardFocus = () => this.#keyboardFocus && this.matches(':focus-within');

  /** @param {Element} element */
  #focusVisible = (element) => {
    try {
      return element.matches(':focus-visible');
    } catch {
      return true;
    }
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    const returning = event.target === this.#keptFocus;
    this.#keptFocus = null;
    if (!returning) this.#keyboardFocus = this.#focusVisible(/** @type {Element} */ (event.target));
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    // Only the window lost focus: the element is still the document's active element.
    if (document.activeElement === event.target) {
      this.#keptFocus = event.target;
      return;
    }
    this.#keptFocus = null;
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#keyboardFocus = false;
  };

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      // THEODORE: the outgoing message stays visible through its 0.5 s fade, and a Tab that
      // reached its link then lost focus when the fade ended; inert keeps Tab off it (bug hunt #17).
      slide.toggleAttribute('inert', index !== relativeIndex);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   * THEODORE: suspend, not pause. pause() sets the lasting paused flag, which
   * resume() refuses to clear, so one tab switch stopped the bar for good (bug hunt #226).
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
